/**
 * The environment a person declares, as the document the repository sheet
 * shows in YAML (spec 2026-10-30, declared environment). It is friendlier than
 * the stored configuration and maps to it both ways: repositories are named
 * `owner/name`, defaults are left out, and every stored key the document has
 * no section for round-trips verbatim under `advanced`. What the platform or a
 * credential decides (network approvals, runtimes, Cognito, preview access)
 * is never in the document.
 *
 * This module runs in the browser too, so it uses nothing from Node.
 */
import { z } from "zod";
import { composeFilePathSchema, composeServiceName, qaCommandSchema, qaDeclaredToolSchema, qaEnvironmentDefaults, qaEnvironmentSchema, qaPathSchema, qaPortSchema, qaReadSchema, qaServiceNameSchema, qaVariables, variableName, } from "./qaEnvironmentSchema.js";
/** A value under this name is a credential, never a default in the document. */
const SECRET_LOOKING_NAME = /KEY|TOKEN|SECRET|PASSWORD/i;
/** Stored keys the document never carries: the platform or a credential decides them. */
const NEVER_IN_DOCUMENT = new Set([
    "version",
    "previewAccess",
    "cognitoAuth",
    "allowedOrigins",
    "authOrigins",
    "installationOrigins",
    "runtimes",
    "toolchain",
    "testDataConfirmed",
]);
/** Sign-in a person sets on the form: kept out of the document for these modes. */
const FORM_ONLY_AUTH_MODES = new Set(["human", "cognito"]);
const FORM_ONLY_AUTH_KEYS = ["authMode", "authCheckSelector", "authStateOrigins"];
/** Stored keys a document section maps; `advanced` may not name them. */
const MAPPED_KEYS = new Set([
    "environmentFrom",
    "reads",
    "declaredTools",
    "toolInstallCommands",
    "requiredVariables",
    "environmentDefaults",
    "generatedVariables",
    "postgres",
    "redis",
    "mysql",
    "dependencies",
    "setupCommands",
    "verifyCommand",
    "services",
]);
const STORED_KEYS = new Set(Object.keys(qaEnvironmentSchema.shape));
/** A repository named as `owner/name`, resolved by the caller. */
const repository = z.string().trim().min(1).max(200);
const commands = z.array(qaCommandSchema).max(20);
const cwd = z
    .string()
    .max(200)
    .refine((value) => !value.startsWith("/") && !value.split("/").includes("..") && !/[\r\n\\]/.test(value), "Name a directory inside the repository");
const database = z.object({ port: qaPortSchema }).strict();
/** Why `advanced` cannot carry `key`, or null when it can. */
function refusalFor(key, value) {
    if (NEVER_IN_DOCUMENT.has(key))
        return `${key} is set by the platform or on the form, never in the environment file`;
    if (key === "environmentFrom")
        return value === null ? null : "Name the environment's repository under environment.from";
    if (MAPPED_KEYS.has(key))
        return `${key} has its own section in the environment file`;
    if (!STORED_KEYS.has(key))
        return `${key} is not an environment setting`;
    return null;
}
export const environmentDocumentSchema = z
    .object({
    version: z.literal(1).optional(),
    environment: z
        .object({
        from: repository,
        compose: composeFilePathSchema.optional(),
        services: z.array(composeServiceName).max(20).optional(),
    })
        .strict()
        .optional(),
    reads: z.array(qaReadSchema).max(20).optional(),
    tools: z
        .object({
        versions: z.array(qaDeclaredToolSchema).max(40).optional(),
        install: commands.optional(),
    })
        .strict()
        .optional(),
    env: z
        .object({
        required: z.array(variableName).max(50).optional(),
        defaults: qaVariables.optional(),
        generated: z
            .array(z.object({ name: variableName, encoding: z.enum(["hex", "base64"]) }).strict())
            .max(50)
            .optional(),
    })
        .strict()
        .optional(),
    databases: z
        .object({
        postgres: database.optional(),
        redis: database.optional(),
        mysql: database.optional(),
    })
        .strict()
        .optional(),
    dependencies: z
        .array(z
        .object({
        repository,
        ref: z
            .string()
            .trim()
            .min(1)
            .max(250)
            .regex(/^[^\s\x00-\x1f]+$/),
        pin: z.enum(["fixed", "branch"]).optional(),
        setup: commands.optional(),
        env: qaVariables.optional(),
    })
        .strict())
        .max(20, "Check out at most 20 dependency repositories")
        .optional(),
    setup: commands.optional(),
    verify: qaCommandSchema.nullable().optional(),
    services: z
        .array(z
        .object({
        name: qaServiceNameSchema,
        command: qaCommandSchema,
        cwd: cwd.optional(),
        port: qaPortSchema,
        readiness: qaPathSchema.optional(),
        readinessTimeoutMs: z.number().int().min(1).optional(),
        repository: repository.optional(),
        env: qaVariables.optional(),
    })
        .strict())
        .max(8)
        .optional(),
    advanced: z
        .record(z.string(), z.unknown())
        .superRefine((values, ctx) => {
        for (const [key, value] of Object.entries(values)) {
            const message = refusalFor(key, value);
            if (message)
                ctx.addIssue({ code: "custom", path: [key], message });
        }
    })
        .optional(),
})
    .strict();
/** A document that cannot become a configuration, at the document path that says why. */
export class EnvironmentDocumentError extends Error {
    path;
    constructor(path, message) {
        super(message);
        this.name = "EnvironmentDocumentError";
        this.path = path;
    }
}
const ZERO_SHA = "0".repeat(40);
/**
 * Refuses a secret-looking name with a value, unless the value is the one the
 * configuration already holds there: a stored default round-trips through the
 * document, and a new secret is never typed into it.
 */
function refuseNewSecrets(values, held, path) {
    for (const [name, value] of Object.entries(values ?? {})) {
        if (!value || !SECRET_LOOKING_NAME.test(name) || held?.[name] === value)
            continue;
        throw new EnvironmentDocumentError([...path, name], `${name} looks like a secret; put it under Connections as a workspace variable`);
    }
}
function same(a, b) {
    return JSON.stringify(a) === JSON.stringify(b);
}
function sameRepository(a, b) {
    return a.trim().toLowerCase() === b.trim().toLowerCase();
}
/**
 * A configuration as its document. Fields at their defaults are left out so
 * the document reads as what is particular about this repository; a
 * repository id `repoName` cannot name becomes `unknown:<id>`.
 */
export function documentFromConfig(config, repoName) {
    const name = (repoId) => repoName(repoId) ?? `unknown:${repoId}`;
    const doc = {};
    if (config.environmentFrom) {
        const from = config.environmentFrom;
        doc.environment = {
            from: name(from.repoId),
            compose: from.composeFile,
            ...(from.services.length ? { services: from.services } : {}),
        };
    }
    if (config.reads.length)
        doc.reads = config.reads;
    if (config.declaredTools.length || config.toolInstallCommands.length)
        doc.tools = {
            ...(config.declaredTools.length ? { versions: config.declaredTools } : {}),
            ...(config.toolInstallCommands.length ? { install: config.toolInstallCommands } : {}),
        };
    const defaults = config.environmentDefaults ?? {};
    const generated = config.generatedVariables ?? [];
    if (config.requiredVariables.length || Object.keys(defaults).length || generated.length)
        doc.env = {
            ...(config.requiredVariables.length ? { required: config.requiredVariables } : {}),
            ...(Object.keys(defaults).length ? { defaults } : {}),
            ...(generated.length ? { generated } : {}),
        };
    if (config.postgres || config.redis || config.mysql)
        doc.databases = {
            ...(config.postgres ? { postgres: config.postgres } : {}),
            ...(config.redis ? { redis: config.redis } : {}),
            ...(config.mysql ? { mysql: config.mysql } : {}),
        };
    if (config.dependencies?.length)
        doc.dependencies = config.dependencies.map((dependency) => ({
            repository: name(dependency.repoId),
            ref: dependency.ref,
            ...(dependency.pin === "branch" ? { pin: "branch" } : {}),
            ...(dependency.setupCommands.length ? { setup: dependency.setupCommands } : {}),
            ...(dependency.environmentDefaults && Object.keys(dependency.environmentDefaults).length
                ? { env: dependency.environmentDefaults }
                : {}),
        }));
    if (config.setupCommands.length)
        doc.setup = config.setupCommands;
    if (config.verifyCommand)
        doc.verify = config.verifyCommand;
    if (config.services.length)
        doc.services = config.services.map((service) => ({
            name: service.name,
            command: service.command,
            ...(service.cwd !== "." ? { cwd: service.cwd } : {}),
            port: service.port,
            ...(service.readinessPath !== "/" ? { readiness: service.readinessPath } : {}),
            ...(service.readinessTimeoutMs !== undefined
                ? { readinessTimeoutMs: service.readinessTimeoutMs }
                : {}),
            ...(service.repoId ? { repository: name(service.repoId) } : {}),
            ...(service.env && Object.keys(service.env).length ? { env: service.env } : {}),
        }));
    const stored = qaEnvironmentDefaults();
    const formOnlyAuth = FORM_ONLY_AUTH_MODES.has(config.authMode ?? "");
    const advanced = {};
    for (const [key, value] of Object.entries(config)) {
        if (value === undefined || NEVER_IN_DOCUMENT.has(key))
            continue;
        if (formOnlyAuth && FORM_ONLY_AUTH_KEYS.includes(key))
            continue;
        // A person cleared the sibling environment: only `advanced` can say so.
        if (key === "environmentFrom") {
            if (value === null)
                advanced.environmentFrom = null;
            continue;
        }
        if (MAPPED_KEYS.has(key) || same(value, stored[key]))
            continue;
        advanced[key] = value;
    }
    if (Object.keys(advanced).length)
        doc.advanced = advanced;
    return doc;
}
/** Each dependency with the repository its name resolves to, in document order. */
function resolveDependencies(doc, ctx) {
    return (doc.dependencies ?? []).map((dependency, index) => {
        const repo = ctx.resolve(dependency.repository);
        if (!repo)
            throw new EnvironmentDocumentError(["dependencies", index, "repository"], `No repository named ${dependency.repository} in this workspace`);
        return { dependency, repo };
    });
}
/** Refuses a new secret anywhere the document sets variables: env, a service's, a dependency's. */
function refuseSecretsInDocument(doc, ctx, resolved) {
    refuseNewSecrets(doc.env?.defaults, ctx.current?.environmentDefaults, ["env", "defaults"]);
    for (const [index, service] of (doc.services ?? []).entries())
        refuseNewSecrets(service.env, ctx.current?.services.find((held) => held.name === service.name)?.env, ["services", index, "env"]);
    for (const [index, { dependency, repo }] of resolved.entries())
        refuseNewSecrets(dependency.env, ctx.current?.dependencies?.find((held) => held.repoId === repo.repoId)?.environmentDefaults, ["dependencies", index, "env"]);
}
/**
 * What the document never carries, kept from the configuration it replaces:
 * approvals, the toolchain plan, and a form-only sign-in the document does not
 * switch away from.
 */
function keepPlatformFields(doc, ctx) {
    const kept = {};
    const current = ctx.current;
    if (!current)
        return kept;
    for (const key of ["previewAccess", "cognitoAuth", "allowedOrigins", "authOrigins", "toolchain"])
        if (current[key] !== undefined)
            kept[key] = current[key];
    if (FORM_ONLY_AUTH_MODES.has(ctx.current?.authMode ?? "") && doc.advanced?.authMode === undefined)
        for (const key of FORM_ONLY_AUTH_KEYS)
            if (current[key] !== undefined)
                kept[key] = current[key];
    return kept;
}
/**
 * A document as the input `qaEnvironmentSchema.parse` takes. `advanced` goes
 * first and every mapped section over it. Repositories resolve through
 * `ctx.resolve`; one that does not is an `EnvironmentDocumentError` at its
 * document path.
 */
export function configFromDocument(doc, ctx) {
    const resolved = resolveDependencies(doc, ctx);
    refuseSecretsInDocument(doc, ctx, resolved);
    const dependencies = resolved.map(({ dependency, repo }) => ({
        repoId: repo.repoId,
        ref: dependency.ref,
        sha: repo.sha ?? ZERO_SHA,
        pin: dependency.pin ?? "fixed",
        setupCommands: dependency.setup ?? [],
        ...(dependency.env ? { environmentDefaults: dependency.env } : {}),
    }));
    const declared = new Map(resolved.map(({ dependency, repo }) => [dependency.repository.trim().toLowerCase(), repo]));
    let environmentFrom;
    if (doc.environment) {
        const from = doc.environment.from;
        if (!ctx.environmentFrom)
            throw new EnvironmentDocumentError(["environment", "from"], "An environment from another repository can only be read when the environment is saved");
        const repo = declared.get(from.trim().toLowerCase()) ?? ctx.resolve(from);
        if (!repo)
            throw new EnvironmentDocumentError(["environment", "from"], `No repository named ${from} in this workspace`);
        environmentFrom = ctx.environmentFrom(doc.environment, repo);
        if (!dependencies.some((dependency) => dependency.repoId === repo.repoId)) {
            dependencies.push({
                repoId: repo.repoId,
                ref: repo.defaultRef ?? "main",
                sha: repo.sha ?? ZERO_SHA,
                pin: "fixed",
                setupCommands: [],
            });
            declared.set(from.trim().toLowerCase(), repo);
        }
    }
    const services = (doc.services ?? []).map((service, index) => {
        let repoId;
        if (service.repository) {
            const name = service.repository;
            const repo = [...declared.entries()].find(([key]) => sameRepository(key, name))?.[1];
            if (!repo)
                throw new EnvironmentDocumentError(["services", index, "repository"], `${name} is not one of this environment's dependencies; add it under dependencies`);
            repoId = repo.repoId;
        }
        return {
            ...(repoId ? { repoId } : {}),
            name: service.name,
            command: service.command,
            cwd: service.cwd ?? ".",
            port: service.port,
            readinessPath: service.readiness ?? "/",
            ...(service.readinessTimeoutMs !== undefined
                ? { readinessTimeoutMs: service.readinessTimeoutMs }
                : {}),
            ...(service.env ? { env: service.env } : {}),
        };
    });
    return {
        ...(doc.advanced ?? {}),
        ...keepPlatformFields(doc, ctx),
        installationOrigins: ctx.installationOrigins,
        runtimes: ctx.runtimes,
        ...(environmentFrom ? { environmentFrom } : {}),
        reads: doc.reads ?? [],
        declaredTools: doc.tools?.versions ?? [],
        toolInstallCommands: doc.tools?.install ?? [],
        requiredVariables: doc.env?.required ?? [],
        ...(doc.env?.defaults ? { environmentDefaults: doc.env.defaults } : {}),
        ...(doc.env?.generated ? { generatedVariables: doc.env.generated } : {}),
        postgres: doc.databases?.postgres ?? null,
        redis: doc.databases?.redis ?? null,
        mysql: doc.databases?.mysql ?? null,
        ...(dependencies.length ? { dependencies } : {}),
        setupCommands: doc.setup ?? [],
        verifyCommand: doc.verify ?? null,
        services,
    };
}
//# sourceMappingURL=environmentDocument.js.map